import { useState, useCallback } from "react";
import type { ColumnOrderState, OnChangeFn } from "@tanstack/react-table";

interface UseColumnOrderingOptions {
  enabled?: boolean;
  columnOrder?: ColumnOrderState;
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>;
}

export const useColumnOrdering = ({
  enabled = false,
  columnOrder: controlledOrder,
  onColumnOrderChange: controlledOnChange,
}: UseColumnOrderingOptions) => {
  const isControlled = controlledOrder !== undefined;

  const [internalOrder, setInternalOrder] = useState<ColumnOrderState>([]);

  const columnOrder = isControlled ? controlledOrder : internalOrder;

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    updaterOrValue => {
      const newOrder =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnOrder)
          : updaterOrValue;

      if (!isControlled) {
        setInternalOrder(newOrder);
      }

      controlledOnChange?.(updaterOrValue);
    },
    [columnOrder, isControlled, controlledOnChange]
  );

  const moveColumn = useCallback(
    (columnId: string, direction: -1 | 1) => {
      onColumnOrderChange(previousOrder => {
        const order = [...previousOrder];
        const currentIndex = order.indexOf(columnId);

        if (currentIndex === -1) return previousOrder;

        const newIndex = currentIndex + direction;
        if (newIndex < 0 || newIndex >= order.length) return previousOrder;

        order.splice(currentIndex, 1);
        order.splice(newIndex, 0, columnId);

        return order;
      });
    },
    [onColumnOrderChange]
  );

  const reorderColumn = useCallback(
    (fromId: string, toId: string) => {
      onColumnOrderChange(previousOrder => {
        const order = [...previousOrder];
        const fromIndex = order.indexOf(fromId);
        const toIndex = order.indexOf(toId);

        if (fromIndex === -1 || toIndex === -1) return previousOrder;

        order.splice(fromIndex, 1);
        order.splice(toIndex, 0, fromId);

        return order;
      });
    },
    [onColumnOrderChange]
  );

  return {
    columnOrder: enabled ? columnOrder : [],
    onColumnOrderChange: enabled
      ? onColumnOrderChange
      : ((() => {}) as OnChangeFn<ColumnOrderState>),
    moveColumn,
    reorderColumn,
  };
};
